import { motion } from "framer-motion";
import { Archive, BookOpen, Camera, Award, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export const HeritageBridge = () => {
  return (
    <section
      id="heritage-bridge"
      data-testid="heritage-bridge-section"
      className="py-20 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(226, 199, 146, 0.1) 0%, rgba(6, 11, 25, 1) 50%, rgba(226, 199, 146, 0.05) 100%)'
      }}
    >
      {/* Vintage texture overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="w-12 h-[1px] bg-gradient-to-r from-secondary to-transparent" />
              <span className="text-secondary/90 text-sm font-medium tracking-[0.2em] uppercase">
                Our Legacy
              </span>
            </div>

            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              Explore Our{" "}
              <span className="text-secondary">Heritage Archive</span>
            </h2>

            <p className="text-white/60 leading-relaxed mb-8">
              Discover the rich history that built our foundation. From our first 
              students to global milestones, explore the journey that shaped who we are today.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/heritage"
                data-testid="heritage-cta"
                className="inline-flex items-center gap-2 px-6 py-3 bg-secondary text-[#060B19] rounded-lg font-semibold hover:bg-secondary/90 transition-all group"
              >
                <Archive className="w-5 h-5" />
                Enter Heritage Archive
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/heritage/timeline"
                className="inline-flex items-center gap-2 px-6 py-3 border border-secondary/30 text-secondary rounded-lg font-medium hover:bg-secondary/10 transition-all"
              >
                View Timeline
              </Link>
            </div>
          </motion.div>

          {/* Right Grid */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-2 gap-4"
          >
            {[
              { icon: BookOpen, label: "Our Story", count: "Since 2013" },
              { icon: Camera, label: "Photo Archive", count: "500+ Photos" },
              { icon: Award, label: "Milestones", count: "50+ Achievements" },
              { icon: Archive, label: "Legacy Content", count: "Full Archive" },
            ].map((item, idx) => (
              <Link
                key={idx}
                to="/heritage"
                className="p-6 rounded-xl glass-card hover:bg-white/10 transition-all group"
              >
                <div className="w-12 h-12 rounded-lg bg-secondary/10 border border-secondary/20 flex items-center justify-center mb-4 group-hover:bg-secondary/20 transition-all">
                  <item.icon className="w-6 h-6 text-secondary" />
                </div>
                <h4 className="font-semibold text-white mb-1">{item.label}</h4>
                <p className="text-sm text-white/50">{item.count}</p>
              </Link>
            ))}
          </motion.div>
        </div>

        {/* Transition Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 p-6 rounded-xl bg-gradient-to-r from-secondary/10 via-transparent to-secondary/10 border border-secondary/20"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
              <p className="text-white/80 text-center md:text-left">
                <span className="text-secondary font-semibold">Welcome to the next chapter.</span>{" "}
                Our legacy continues with a new digital experience designed for today's global citizens.
              </p>
            </div>
            <Link
              to="/transition"
              className="text-secondary text-sm font-medium hover:underline whitespace-nowrap"
            >
              Learn about the transition →
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
